import type { JobCardProps } from "./JobCard";
import type { JobCardDataWithBrandingEligibility } from "./types";

import { ActionButtonsWrapper } from "./ActionButtons/ActionButtonsWrapper";
import {
  ApplicationStatusBadge,
  hasApplicationStatusBadge,
} from "./ApplicationStatusBadge/ApplicationStatusBadge";
import { AttributeComponent } from "./Attribute/AttributeComponent";
import { AttributesContainer } from "./Attribute/AttributesContainer";
import { SalaryAttributeComponent } from "./Attribute/SalaryAttributeComponent";
import { useBrandingMigrationFlags } from "./BrandingMigrationContext";
import { BANNER_IMAGE_HEIGHT, CompanyBannerImage } from "./CompanyBanner/CompanyBannerImage";
import { CompanyInfo, getLocationAccessibilityText } from "./CompanyInfo/CompanyInfo";
import { CompanyLogo } from "./CompanyInfo/CompanyLogo";
import { EnticerSignalsContainer } from "./EnticerSignals/EnticerSignalsContainer";
import { FlairTags, useFlairAccessibilityLabel } from "./FlairTags/FlairTags";
import { JobCardHeading } from "./JobCardHeader/JobCardHeading";
import { RelevanceSurvey } from "./RelevanceSurvey/RelevanceSurvey";
import { SponsoredDisclaimer } from "./SponsoredDisclaimer/SponsoredDisclaimer";
import { useLogFeatureDisplay } from "../../../homepage/logging/DisplayLog";
import { type BoxRef, Box, Pressable } from "../../../mads";
import { useI18n } from "../../../shared/I18n";

import { colors } from "@indeed/ifl-tokens/react-native";
import { useCallback, useMemo, useState } from "react";
import { Platform, StyleSheet } from "react-native";

type VisibleAttributeLabel = {
  label: string;
  order: number;
  isPositive: boolean;
};

type VisibleAttributeLabelsState = {
  jobKey: string;
  labelsById: Record<string, VisibleAttributeLabel>;
};

export function DefaultJobCard({
  ref,
  pressed,
  saveButton,
  dislikeButton,
  jobCardProps,
  viewFullDetailsLabel,
  onPressJobCard,
  trackingKey,
  showApplicationStatusBadge = false,
  isSelected = false,
}: {
  ref?: React.Ref<BoxRef>;
  pressed: boolean;
  saveButton: React.ReactNode;
  dislikeButton: React.ReactNode;
  jobCardProps: JobCardProps;
  viewFullDetailsLabel: string;
  onPressJobCard?: () => void;
  trackingKey?: string;
  showApplicationStatusBadge?: boolean;
  isSelected?: boolean;
}) {
  const {
    data: job,
    clickEffectEnabled,
    loggedIn,
    appearanceMode,
    showRelevanceSurvey,
    position,
    enticerSignals = [],
    hideActionButtons,
    bannerImageUrl,
    preserveBannerAspectRatio,
  } = jobCardProps;
  const i18n = useI18n();
  const { isBrandedJobMigrationTestActive } = useBrandingMigrationFlags();
  // Mirror the inner accessibility Pressable state so the full card shows pressed feedback.
  const [innerPressed, setInnerPressed] = useState(false);
  const [visibleLabelsState, setVisibleLabelsState] = useState<VisibleAttributeLabelsState>({
    jobKey: job.key,
    labelsById: {},
  });
  const flairLabel = useFlairAccessibilityLabel(job);
  const href =
    jobCardProps?.webTargetUrl === null
      ? undefined
      : (jobCardProps?.webTargetUrl ?? `/viewjob?jobKey=${job.key}`);

  const handleAttributeTagVisibilityChanged = useCallback(
    (id: string, label: string, order: number, isVisible: boolean, isPositive: boolean) => {
      setVisibleLabelsState((previousState) => {
        const labelsById = previousState.jobKey === job.key ? previousState.labelsById : {};

        if (!isVisible) {
          if (!labelsById[id]) {
            return previousState.jobKey === job.key
              ? previousState
              : { jobKey: job.key, labelsById };
          }

          const nextLabelsById = { ...labelsById };
          delete nextLabelsById[id];
          return { jobKey: job.key, labelsById: nextLabelsById };
        }

        const currentLabel = labelsById[id];
        if (
          previousState.jobKey === job.key &&
          currentLabel?.label === label &&
          currentLabel.order === order &&
          currentLabel.isPositive === isPositive
        ) {
          return previousState;
        }

        return {
          jobKey: job.key,
          labelsById: {
            ...labelsById,
            [id]: { label, order, isPositive },
          },
        };
      });
    },
    [job.key],
  );

  const locationLabel = getLocationAccessibilityText({ job, i18n });

  const hasApplicationBadge = hasApplicationStatusBadge(
    job.savedJob?.state,
    job.savedJob?.applicationDraft,
  );
  const showBadge = showApplicationStatusBadge && hasApplicationBadge;

  const viewFullDetailsAccessibilityLabel = useMemo(() => {
    const visibleLabelsById =
      visibleLabelsState.jobKey === job.key ? visibleLabelsState.labelsById : {};
    const visiblePillLabels = Object.values(visibleLabelsById)
      .sort((a, b) => a.order - b.order)
      .map(({ label, isPositive }) => {
        if (isPositive) return i18n.gettext("{0}, matches your preferences", label);

        return label;
      });

    // The badge replaces the flair tags visually, so drop the flair text to match.
    const accessibleFlairLabel = showBadge ? "" : flairLabel;

    // Visual top-to-bottom order: flairs, title + employer, location, then pills.
    return [accessibleFlairLabel, viewFullDetailsLabel, locationLabel, ...visiblePillLabels]
      .filter((label) => label.length > 0)
      .join(", ");
  }, [
    flairLabel,
    locationLabel,
    job.key,
    showBadge,
    viewFullDetailsLabel,
    i18n,
    visibleLabelsState,
  ]);

  const isFeatured = appearanceMode === "featured";
  const featuredData = job.featuredCardData;
  const jobWithBranding = job as JobCardDataWithBrandingEligibility;
  const subgraphBanner = isBrandedJobMigrationTestActive
    ? jobWithBranding.brandingEligibility?.shouldShowBranding
      ? jobWithBranding.brandingEligibility?.brandingInformation?.localizedAssets?.banner?.url
      : undefined
    : undefined;
  const bannerUrl = isFeatured
    ? (bannerImageUrl ??
      (isBrandedJobMigrationTestActive ? subgraphBanner : featuredData?.headerImageUrl))
    : null;
  const hasBanner = typeof bannerUrl === "string" && bannerUrl.length > 0;
  // The 1960:400 frame is the contract of the surface-provided bannerImageUrl only.
  // Fallback sources (subgraph / featuredCardData) have no such guarantee, so keep
  // them on the fixed-height path even when the surface asks to preserve aspect ratio.
  const usedSurfaceBanner = isFeatured && !!bannerImageUrl;
  const hasEnticers = isFeatured && enticerSignals.length > 0;

  const logFeatureDisplay = useLogFeatureDisplay();
  logFeatureDisplay({
    jobKey: job.key,
    field: "isFeaturedJobCardShown",
    value: isFeatured,
  });
  logFeatureDisplay({
    jobKey: job.key,
    field: "isTopRatedEmployerYN",
    value: !!job.source?.employerActivity,
  });
  logFeatureDisplay({
    jobKey: job.key,
    field: "companyBannerYN",
    value: hasBanner,
  });
  if (hasEnticers) {
    logFeatureDisplay({
      jobKey: job.key,
      field: "enticersShown",
      value: enticerSignals.map((signal) => signal.type),
    });
  }

  return (
    <Box
      pointerEvents="box-none"
      sx={styles.outerWrapper}
      {...Platform.select({ web: href ? { href, target: "_self" } : {} })}
    >
      <Box
        testID="job-card-container"
        sx={[
          styles.container,
          isSelected ? styles.containerSelected : null,
          clickEffectEnabled && (pressed || innerPressed) ? styles.containerPressed : null,
        ]}
      >
        <Pressable
          ref={ref}
          accessible={true}
          accessibilityRole="button"
          role="button"
          testID="inner-view-details-pressable"
          accessibilityLabel={viewFullDetailsAccessibilityLabel}
          accessibilityHint={i18n.gettext("Tap to view job details")}
          onPress={onPressJobCard}
          onPressIn={() => setInnerPressed(true)}
          onPressOut={() => setInnerPressed(false)}
        >
          <Box accessibilityElementsHidden={true} importantForAccessibility="no-hide-descendants">
            {hasBanner && (
              <CompanyBannerImage
                url={bannerUrl}
                preserveAspectRatio={preserveBannerAspectRatio && usedSurfaceBanner}
              />
            )}
            <Box sx={styles.contentBelowBanner}>
              {/* Header */}
              <Box sx={styles.header}>
                <Box sx={styles.headerLeft}>
                  {/* Flair tags */}
                  {!showBadge && <FlairTags job={job} />}

                  {/* Application Status Badge */}
                  {showApplicationStatusBadge && (
                    <ApplicationStatusBadge
                      savedJobState={job.savedJob?.state}
                      applicationDraft={job.savedJob?.applicationDraft}
                    />
                  )}
                  {/* Title */}
                  <JobCardHeading
                    job={job}
                    pressed={pressed}
                    clickEffectEnabled={clickEffectEnabled}
                    appearanceMode={"default"}
                  />
                  {/* Company Info */}
                  <Box sx={[styles.company]}>
                    <CompanyLogo job={job} />
                    <CompanyInfo job={job} appearanceMode={"default"} />
                  </Box>
                </Box>
              </Box>
              {/* Job attributes */}
              <AttributesContainer appearanceMode={"default"}>
                <SalaryAttributeComponent
                  salary={job.compensation?.formattedText ?? ""}
                  jobKey={job.key}
                  isHighlightPositive={job.jobseekerPreferences?.paySentiment === "POSITIVE"}
                  tagVisibilityId={`${job.key}-salary`}
                  tagVisibilityOrder={0}
                  onTagVisibilityChanged={handleAttributeTagVisibilityChanged}
                />
                <AttributeComponent
                  attributes={job.jobTypes}
                  type="jobTypes"
                  jobKey={job.key}
                  tagVisibilityOrderStart={1}
                  onTagVisibilityChanged={handleAttributeTagVisibilityChanged}
                />
                <AttributeComponent
                  attributes={job.shiftAndSchedule}
                  type="shiftAndSchedule"
                  jobKey={job.key}
                  tagVisibilityOrderStart={2}
                  onTagVisibilityChanged={handleAttributeTagVisibilityChanged}
                />
                <AttributeComponent
                  attributes={job.benefits}
                  type="benefits"
                  jobKey={job.key}
                  tagVisibilityOrderStart={3}
                  onTagVisibilityChanged={handleAttributeTagVisibilityChanged}
                />
              </AttributesContainer>
              {/* EU sponsored disclaimer (renders nothing when not sponsored) */}
              <SponsoredDisclaimer job={job} />
            </Box>
          </Box>
        </Pressable>
        {!hideActionButtons && (
          <ActionButtonsWrapper
            sx={[styles.actionButtonsAbsolute, hasBanner && styles.actionButtonsBelowBanner]}
            loggedIn={loggedIn}
            jobTitle={job.title}
          >
            <Box sx={styles.dislikeButtonWrapper}>{saveButton}</Box>
            <Box>{dislikeButton}</Box>
          </ActionButtonsWrapper>
        )}
        {showRelevanceSurvey && (
          <RelevanceSurvey jobKey={job.key} trackingKey={trackingKey} position={position} />
        )}
        {/* TODO: Job Status, cf: https://indeed.sourcegraph.com/code.corp.indeed.com/mobile-app-platform/mobile-app-platform@1db2331a32c92d6c3248f2c573e750ee1e56003b/-/blob/apps/jsma-ios/Packages/CoworkerPlugin/Sources/CoworkerPlugin/Views/JobCardViews/JobCardSerp.swift?L81 */}
        {/* Enticer signals for featured cards */}
        {hasEnticers && <EnticerSignalsContainer signals={enticerSignals} />}
      </Box>
    </Box>
  );
}

const styles = StyleSheet.create({
  outerWrapper: {
    flexGrow: 1,
  },
  container: {
    flexGrow: 1,
    padding: 16,
    backgroundColor: "white",
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.borderWeak,
    overflow: "hidden",
    position: "relative",
  },
  containerPressed: {
    backgroundColor: colors.backgroundStrong,
  },
  containerSelected: {
    borderColor: colors.borderPrimary,
    borderWidth: 2,
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  contentBelowBanner: {
    paddingEnd: 36,
  },
  headerLeft: {
    flex: 1,
  },
  actionButtonsAbsolute: {
    position: "absolute",
    top: 16,
    end: 16,
  },
  actionButtonsBelowBanner: {
    top: BANNER_IMAGE_HEIGHT + 16,
  },
  company: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    gap: 8,
  },
  dislikeButtonWrapper: {
    marginBottom: 16,
  },
});
